/**
 * The step table's column widths, dragged at a heading's right edge: the
 * names column, and one width for every step, so dragging any step's edge
 * sizes them all alike. Kept in the browser, as a view preference. The edge
 * takes the arrow keys too, and a double-click puts the default back.
 */
import { useRef, useState, type KeyboardEvent, type PointerEvent } from "react";
import {
  clampStepTable,
  loadStepTableWidths,
  saveStepTableWidths,
  STEP_TABLE,
  STEP_TABLE_DEFAULTS,
  type StepTableWidths,
} from "./preferences";

type Column = keyof StepTableWidths;

const KEY_STEP = 8;

export interface StepTableSizing {
  widths: StepTableWidths;
  /** Set a column's width as it is dragged; `keep` stores it. */
  set: (column: Column, width: number, keep: boolean) => void;
}

export function useStepTableWidths(): StepTableSizing {
  const [widths, setWidths] = useState(loadStepTableWidths);
  const set = (column: Column, width: number, keep: boolean) => {
    setWidths((current) => {
      const next = { ...current, [column]: clampStepTable(column, width) };
      if (keep) saveStepTableWidths(next);
      return next;
    });
  };
  return { widths, set };
}

/** The drag edge at the right of a heading, sizing `column`. */
export function WidthHandle({ sizing, column, label }: { sizing: StepTableSizing; column: Column; label: string }) {
  const drag = useRef<{ x: number; from: number; now: number } | null>(null);
  const width = sizing.widths[column];
  const start = (event: PointerEvent<HTMLSpanElement>) => {
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    drag.current = { x: event.clientX, from: width, now: width };
  };
  const move = (event: PointerEvent<HTMLSpanElement>) => {
    const now = drag.current;
    if (!now) return;
    now.now = now.from + event.clientX - now.x;
    sizing.set(column, now.now, false);
  };
  const end = () => {
    const now = drag.current;
    if (!now) return;
    drag.current = null;
    sizing.set(column, now.now, true);
  };
  const key = (event: KeyboardEvent<HTMLSpanElement>) => {
    const delta = event.key === "ArrowLeft" ? -KEY_STEP : event.key === "ArrowRight" ? KEY_STEP : 0;
    if (!delta) return;
    event.preventDefault();
    sizing.set(column, width + delta, true);
  };
  return (
    <span
      className="ws-col-resize"
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuenow={width}
      aria-valuemin={STEP_TABLE[column].minimum}
      aria-valuemax={STEP_TABLE[column].maximum}
      tabIndex={0}
      title="Drag to resize; double-click for the default width"
      onPointerDown={start}
      onPointerMove={move}
      onPointerUp={end}
      onPointerCancel={end}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={key}
      onDoubleClick={() => sizing.set(column, STEP_TABLE_DEFAULTS[column], true)}
    />
  );
}
